import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {useMemo} from 'react';

import {fetchAndDeserialize, serializeToURLParameters} from '../../../util/api-adapter';
import {getUniqueListByKey} from '../../../util/array';
import {usePagination} from '../../../util/pagination-hook/pagination-hook';
import {PaginationType} from '../../../util/pagination-hook/pagination-hook-type';
import {objectToUrlParameters} from '../../../util/url';
import {generateResponseSchema, PaginatedResponseType, RequestOptionsType} from '../../api/api-type';

import {defaultCompaniesCountPerPage} from './competitors-const';
import {
    competitorCompaniesResponseSchema,
    CompetitorCompanyType,
    competitorsResponseSchema,
    CompetitorsResponseType,
    CompetitorType,
} from './competitors-type';

export async function fetchCompetitors(): Promise<Array<CompetitorType>> {
    const url = `/v2/reviews-analytics/competitors/`;

    const competitorsResponse: CompetitorsResponseType = await fetchAndDeserialize<CompetitorsResponseType>(
        url,
        competitorsResponseSchema
    );
    const notUniqCompetitors: Array<CompetitorType> = Object.values<Array<CompetitorType>>(competitorsResponse).flat();

    return getUniqueListByKey<CompetitorType>(notUniqCompetitors, 'id');
}

function getCompetitorsCompaniesUrl(
    options: {brandId: number | null; filterKey: string; q: string},
    paginationOptions: RequestOptionsType
) {
    return `/cp/reviews/competitor_companies_info/?${objectToUrlParameters(
        serializeToURLParameters({
            ...options,
            ...paginationOptions,
        })
    )}`;
}

function fetchCompetitorsCompanies(
    options: {
        brandId: number | null;
        filterKey: string;
        q: string;
    },
    paginationOptions: RequestOptionsType
): Promise<PaginatedResponseType<CompetitorCompanyType>> {
    const competitorCompanySchema = competitorCompaniesResponseSchema.shape.results.element;
    const paginatedSchema = generateResponseSchema(competitorCompanySchema);

    return fetchAndDeserialize(getCompetitorsCompaniesUrl(options, paginationOptions), paginatedSchema);
}

export function useCompetitorCompanies(options: {
    brandId: number | null;
    filterKey: string;
    q: string;
}): UseQueryResult<PaginatedResponseType<CompetitorCompanyType>> & PaginationType {
    const {filterKey, q, brandId} = options;

    const cursorPaginationDependencies = useMemo(() => {
        return {filterKey, q, brandId};
    }, [filterKey, q, brandId]);

    const pagination = usePagination({
        dependencies: cursorPaginationDependencies,
        initialPageSize: defaultCompaniesCountPerPage,
        shouldSaveState: false,
    });

    const {page, onDataLoaded} = pagination;

    const paginationOptions: RequestOptionsType = {
        page,
    };

    const query = useQuery(
        [getCompetitorsCompaniesUrl(options, paginationOptions)],
        () => fetchCompetitorsCompanies(options, paginationOptions),
        {
            onSuccess: (data) => onDataLoaded(data),
        }
    );

    return {...query, pagination};
}

function getCompetitorCompanyInfoUrl(companyId: number | null): string {
    return `/cp/reviews/competitor_company/${companyId}/`;
}

function fetchCompetitorCompanyInfo(companyId: number | null): Promise<CompetitorCompanyType> {
    return fetchAndDeserialize(getCompetitorCompanyInfoUrl(companyId), competitorCompaniesResponseSchema);
}

export function useCompetitorCompanyInfo(companyId: number | null): UseQueryResult<CompetitorCompanyType> {
    return useQuery([getCompetitorCompanyInfoUrl(companyId)], () => fetchCompetitorCompanyInfo(companyId), {
        enabled: Boolean(companyId),
    });
}
